import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getLearnerProgressAsync } from '../../Services/ReportService';
import Card from '../../Shared/Components/Card/Card';
import Header from '../../Shared/Components/Header/Header';
import Loading from '../../Shared/Loading';
import AiOverviewCard from './Components/AiOverviewCard';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { formatDateTime } from '../../Infrastructure/Utilities/DateHelper';

const LearnerProgressDashboard = () => {
    const { learnerId } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            if (!learnerId) {
                setError('No learner ID provided');
                setLoading(false);
                return;
            }

            setLoading(true);
            setError(null);

            try {
                const response = await getLearnerProgressAsync(learnerId);
                setData(response);
            } catch (err) {
                console.error("Error fetching learner progress:", err);
                setError('Failed to load learner progress data');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [learnerId]);

    if (loading) {
        return (
            <>
                <Header heading="Learner Progress" subheading="Loading..." icon="user" />
                <div className="container-fluid content">
                    <Card>
                        <Loading />
                    </Card>
                </div>
            </>
        );
    }

    if (error || !data) {
        return (
            <>
                <Header heading="Learner Progress" subheading="Error" icon="user" />
                <div className="container-fluid content">
                    <Card>
                        <div className="alert alert-danger">{error || 'No data available'}</div>
                    </Card>
                </div>
            </>
        );
    }

    const { learner, courseProgress, courses, pathways, recentActivity } = data;

    // Data for pie charts
    const courseStatusData = [
        { name: 'Completed', value: courseProgress.completed, color: '#28a745' },
        { name: 'In Progress', value: courseProgress.inProgress, color: '#ffc107' },
        { name: 'Not Started', value: courseProgress.notStarted, color: '#6c757d' }
    ].filter(item => item.value > 0);

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'Completed': return 'badge-success';
            case 'In Progress': return 'badge-warning';
            case 'Not Started': return 'badge-secondary';
            default: return 'badge-secondary';
        }
    };

    return (
        <>
            <Header
                heading={learner.name}
                subheading="Learner Progress Dashboard"
                icon="user"
            />
            <div className="container-fluid content">
                <AiOverviewCard aiOverview={data.aiOverview} />
                {/* Learner Information */}
                <Card title="Learner Information">
                    <div className="row">
                        <div className="col-md-3">
                            <strong>Email:</strong><br />
                            {learner.email}
                        </div>
                        <div className="col-md-3">
                            <strong>Client:</strong><br />
                            {learner.clientName}
                        </div>
                        <div className="col-md-3">
                            <strong>Group:</strong><br />
                            {learner.groupName || 'N/A'}
                        </div>
                        <div className="col-md-3">
                            <strong>Registered:</strong><br />
                            {formatDateTime(learner.joinedDate)}
                        </div>
                    </div>
                    <div className="row mt-3">
                        <div className="col-md-3">
                            <strong>Last Login:</strong><br />
                            {formatDateTime(learner.lastLogin, 'Never')}
                        </div>
                        <div className="col-md-3">
                            <strong>Days Since Last Login:</strong><br />
                            {learner.daysSinceLastLogin !== null ? learner.daysSinceLastLogin : 'N/A'}
                        </div>
                    </div>
                </Card>

                {/* Course Progress Overview */}
                <div className="row">
                    <div className="col-md-8">
                        <Card title="Course Progress Statistics">
                            <div className="row text-center">
                                <div className="col-md-3 mb-3">
                                    <h3 className="mb-0">{courseProgress.totalEnrolled}</h3>
                                    <small className="text-muted">Total Enrolled</small>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <h3 className="mb-0 text-success">{courseProgress.completed}</h3>
                                    <small className="text-muted">Completed</small>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <h3 className="mb-0 text-warning">{courseProgress.inProgress}</h3>
                                    <small className="text-muted">In Progress</small>
                                </div>
                                <div className="col-md-3 mb-3">
                                    <h3 className="mb-0 text-secondary">{courseProgress.notStarted}</h3>
                                    <small className="text-muted">Not Started</small>
                                </div>
                            </div>
                            <div className="row text-center mt-3 pt-3 border-top">
                                <div className="col-md-3">
                                    <h4 className="mb-0">{courseProgress.completionRate.toFixed(1)}%</h4>
                                    <small className="text-muted">Completion Rate</small>
                                </div>
                                <div className="col-md-3">
                                    <h4 className="mb-0">{courseProgress.averageScore?.toFixed(1) || 'N/A'}</h4>
                                    <small className="text-muted">Average Score</small>
                                </div>
                                <div className="col-md-3">
                                    <h4 className="mb-0">{courseProgress.totalModulesCompleted}</h4>
                                    <small className="text-muted">Modules Completed</small>
                                </div>
                                <div className="col-md-3">
                                    <h4 className="mb-0">{courseProgress.totalHoursSpent.toFixed(1)}</h4>
                                    <small className="text-muted">Hours Spent</small>
                                </div>
                            </div>
                        </Card>
                    </div>

                    <div className="col-md-4">
                        <Card title="Course Status Distribution">
                            {courseStatusData.length > 0 ? (
                                <ResponsiveContainer width="100%" height={250}>
                                    <PieChart>
                                        <Pie
                                            data={courseStatusData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={false}
                                            label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            outerRadius={80}
                                            fill="#8884d8"
                                            dataKey="value"
                                        >
                                            {courseStatusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip />
                                    </PieChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-muted">No course data available</p>
                            )}
                        </Card>
                    </div>
                </div>

                {/* Enrolled Courses */}
                <Card title="Enrolled Courses">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>Course Title</th>
                                    <th>Type</th>
                                    <th>Status</th>
                                    <th>Progress</th>
                                    <th>Score</th>
                                    <th>Modules</th>
                                    <th>Started</th>
                                    <th>Completed</th>
                                </tr>
                            </thead>
                            <tbody>
                                {courses && courses.length > 0 ? (
                                    courses.map((course, index) => (
                                        <tr key={index}>
                                            <td>{course.courseTitle}</td>
                                            <td>{course.hasAssessment ? 'Assessment' : 'Mindfullness'}</td>
                                            <td>
                                                <span className={`badge ${getStatusBadgeClass(course.status)}`}>
                                                    {course.status}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="progress" style={{ height: '20px' }}>
                                                    <div
                                                        className="progress-bar"
                                                        role="progressbar"
                                                        style={{ width: `${course.progress}%` }}
                                                        aria-valuenow={course.progress}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    >
                                                        {course.progress.toFixed(0)}%
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{course.hasAssessment ? (course.score !== null ? course.score.toFixed(1) : 'N/A') : '-'}</td>
                                            <td>{course.modulesCompleted} / {course.modulesTotal}</td>
                                            <td>{formatDateTime(course.startedDate)}</td>
                                            <td>{formatDateTime(course.completedDate)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="8" className="text-center text-muted">No courses enrolled</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Card>

                {/* Learning Pathways */}
                {pathways && pathways.length > 0 && (
                    <Card title="Learning Pathways">
                        <div className="table-responsive">
                            <table className="table table-hover">
                                <thead>
                                    <tr>
                                        <th>Pathway Name</th>
                                        <th>Total Courses</th>
                                        <th>Completed Courses</th>
                                        <th>Completion</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pathways.map((pathway, index) => (
                                        <tr key={index}>
                                            <td>{pathway.pathwayName}</td>
                                            <td>{pathway.totalCourses}</td>
                                            <td>{pathway.completedCourses}</td>
                                            <td>
                                                <div className="progress" style={{ height: '20px' }}>
                                                    <div
                                                        className="progress-bar bg-success"
                                                        role="progressbar"
                                                        style={{ width: `${pathway.completionPercentage}%` }}
                                                        aria-valuenow={pathway.completionPercentage}
                                                        aria-valuemin="0"
                                                        aria-valuemax="100"
                                                    >
                                                        {pathway.completionPercentage.toFixed(0)}%
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* Recent Activity */}
                {recentActivity && recentActivity.events && recentActivity.events.length > 0 && (
                    <Card title="Recent Activity">
                        <div className="table-responsive">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Event Type</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {recentActivity.events.slice(0, 10).map((event, index) => (
                                        <tr key={index}>
                                            <td>{formatDateTime(event.date)}</td>
                                            <td>{event.eventType}</td>
                                            <td>{event.description}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}
            </div>
        </>
    );
};

export default LearnerProgressDashboard;
